import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Alert, Button, Card, Collapse } from 'react-bootstrap';

import "./style.css";
import callApi from "../../api/callApi";
import DataExtractionView from '../DataExtraction/view';


const ViewExtraction = () => {
    const { requestId } = useParams();
    const navigate = useNavigate();
    const [batchResult, setBatchResult] = useState(null);
    const [batchStatus, setBatchStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedCards, setExpandedCards] = useState({ 0: true }); // First card expanded by default

    useEffect(() => {
        loadBatchData();
    }, [requestId]);

    const loadBatchData = async () => {
        try {
            setLoading(true);
            setError(null);

            // First, get the batch request status
            const statusUrl = `${process.env.REACT_APP_API_URL}/v1/portal/extract_statement/batch/${requestId}`;
            const statusResponse = await callApi(statusUrl, 'GET');

            if (!statusResponse.data) {
                setError('Batch request not found');
                setLoading(false);
                return;
            }

            const requestData = statusResponse.data;
            setBatchStatus(requestData);

            // Check if request is still processing
            if (!(requestData.status === 'completed' || requestData.status === 'failed')) {
                console.log(requestData)
                setError(`Batch request is ${requestData.status}. Please check back later when processing is complete.`);
                setLoading(false);
                return;
            }

            if (!requestData.result_stored) {
                setError('Results are not yet available. Please try again in a few moments.');
                setLoading(false);
                return;
            }

            // Get the actual results
            const resultUrl = `${process.env.REACT_APP_API_URL}/v1/portal/extract_statement/batch/${requestId}/result`;
            const resultResponse = await callApi(resultUrl, 'GET');

            if (resultResponse.data) {
                setBatchResult(resultResponse.data);
            } else {
                setError('Failed to load batch results');
            }

        } catch (error) {
            console.error('Error loading batch data:', error);
            setError('Failed to load batch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToViewExtractions = () => {
        navigate('/view-extractions');
    };

    const toggleCardExpansion = (index) => {
        setExpandedCards(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return dateTimeString;
        const [datePart, timePart] = dateTimeString.split('T');
        const [year, month, day] = datePart.split('-');
        const time = timePart.split('.')[0];
        return `${day}-${month}-${year} ${time}`;
    };

    // Format number with thousands separator and no unnecessary decimals
    const formatNumber = (amount, decimals=2) => {
        if (amount === null || amount === undefined) return '';
        const num = parseFloat(amount);
        // Use toLocaleString to add thousands separator
        // Remove decimals if they are .00
        return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };

    if (loading) {
        return (
            <Container fluid className="mt-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container fluid className="mt-4">
                <Row className="justify-content-center">
                    <Col lg={8}>
                        <Alert variant="danger" className="text-center">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                        <div className="text-center">
                            <Button variant="primary" onClick={handleBackToViewExtractions}>
                                <i className="fas fa-arrow-left me-2"></i>
                                Back to View Extractions
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }

    if (!batchResult) {
        return (
            <Container fluid className="mt-4">
                <Row className="justify-content-center">
                    <Col lg={8}>
                        <Alert variant="warning" className="text-center">
                            <i className="fas fa-info-circle me-2"></i>
                            No data available for this request
                        </Alert>
                        <div className="text-center">
                            <Button variant="primary" onClick={handleBackToViewExtractions}>
                                <i className="fas fa-arrow-left me-2"></i>
                                Back to View Extractions
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }

    const { statements = [], summary = {} } = batchResult;

    return (
        <Container fluid className="mt-4 history-page">
            {/* Header with Back Icon */}
            <Row className="justify-content-center mb-3">
                <Col lg={12}>
                    <div className="d-flex justify-content-between align-items-center">
                        <i 
                            className="fas fa-arrow-left back-icon"
                            onClick={handleBackToViewExtractions}
                            title="Back to View Extractions"
                        ></i>
                        <h2 className="mb-0">Request ID: {requestId}</h2>
                        <div className="header-spacer"></div>
                    </div>
                </Col>
            </Row>

            {/* Summary Card */}
            {summary && Object.keys(summary).length > 0 && (
                <Row className="justify-content-center mb-4">
                    <Col lg={12}>
                        <Card className="shadow-sm">
                            <Card.Header className="text-dark" style={{ backgroundColor: '#d1e7dd' }}>
                                <h5 className="mb-0">
                                    <i className="fas fa-chart-bar me-2"></i>
                                    Summary
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={2}>
                                        <div className="text-center">
                                            <h4 className="text-primary mb-0">{summary.processed_files || 0}</h4>
                                            <small className="text-muted">Processed Files</small>
                                        </div>
                                    </Col>
                                    <Col md={2}>
                                        <div className="text-center">
                                            <h4 className="text-success mb-0">{summary.extracted_files || 0}</h4>
                                            <small className="text-muted">Successfully Extracted</small>
                                        </div>
                                    </Col>
                                    <Col md={2}>
                                        <div className="text-center">
                                            <h4 className="text-danger mb-0">{summary.invalid_files || 0}</h4>
                                            <small className="text-muted">Invalid Files</small>
                                        </div>
                                    </Col>
                                    <Col md={2}>
                                        <div className="text-center">
                                            <h4 className="text-success mb-0">{summary.valid_files || 0}</h4>
                                            <small className="text-muted">Valid Files</small>
                                        </div>
                                    </Col>
                                    <Col md={2}>
                                        <div className="text-center">
                                            <h4 className="text-danger mb-0">{summary.failed_files || 0}</h4>
                                            <small className="text-muted">Failed Files</small>
                                        </div>
                                    </Col>
                                    <Col md={2}>
                                        <div className="text-center">
                                            <h4 className={`${batchStatus?.result_stored ? 'text-success' : 'text-danger'} mb-0`}>{batchStatus?.result_stored ? 'Yes' : 'No'}</h4>
                                            <small className="text-muted">Results Stored</small>
                                        </div>
                                    </Col>
                                </Row>
                                {summary.failed_files > 0 && (
                                    <Row className="mt-3">
                                        <Col>
                                            <Alert variant="danger" className="mb-0 text-start">
                                                <strong>
                                                    <i className="fas fa-times-circle me-1"></i>
                                                    {summary.failed_files} file{summary.failed_files > 1 ? 's' : ''} failed to process:
                                                </strong>
                                                <div>{summary.failed_filenames?.join(', ') || 'Unknown files'}</div>
                                            </Alert>
                                        </Col>
                                    </Row>
                                )}
                                {summary.request_start_datetime && summary.request_end_datetime && (
                                    <Row className="mt-3">
                                        <Col md={6} className="text-start">
                                            <p><strong>Request Start Time: </strong>{formatDateTime(summary.request_start_datetime)}</p>
                                            <p><strong>Request End Time: </strong>{formatDateTime(summary.request_end_datetime)}</p>
                                        </Col>
                                        <Col md={6} className="text-start">
                                            <p><strong>Total Accounts: </strong>{summary.total_accounts || 0}</p>
                                            <p><strong>Total Transactions: </strong>{formatNumber(summary.total_transactions, 0) || 0}</p>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Individual Statement Cards */}
            {statements.length > 0 && (
                <Row className="justify-content-center">
                    <Col lg={12}>
                        {statements.map((statement, index) => (
                            <Card key={index} className="shadow-sm mb-3">
                                <Card.Header 
                                    className="bg-light cursor-pointer"
                                    onClick={() => toggleCardExpansion(index)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">
                                            <i className="fas fa-file-alt me-2"></i>
                                            {statements.length === 1 ? 'Statement' : `Statement ${index + 1}`}
                                            {statement.file_name ? `: ${statement.file_name}` : ''}
                                        </h6>
                                        <div className="d-flex align-items-center">
                                            <i className={`fas ${expandedCards[index] ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Collapse in={expandedCards[index]}>
                                    <div>
                                        <Card.Body className="p-0">
                                            <DataExtractionView output={statement} />
                                        </Card.Body>
                                    </div>
                                </Collapse>
                            </Card>
                        ))}
                    </Col>
                </Row>
            )}

            {/* No Statements Message */}
            {statements.length === 0 && (
                <Row className="justify-content-center">
                    <Col lg={8}>
                        <Alert variant="info" className="text-center">
                            <i className="fas fa-info-circle me-2"></i>
                            No statements found in this request
                        </Alert>
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default ViewExtraction;
